import apiClient from '@/api/client';
import axios from 'axios';
import { getActiveHospitalId, getActiveClinicId, ENV } from '@/constants';

const PLATFORM_BASE_URL = ENV.PLATFORM_BACKEND_URL || 'http://localhost:8080';

export const DEFAULT_CLINIC_SETTINGS = {
  // Clinic Preferences
  operatingHours: '08:00 AM - 04:00 PM',
  roomNumbers: 'G-101, G-102',
  allowWalkIn: true,
  triageWaitAlert: true,

  // Appointment Slots
  slotDuration: '30',
  advanceBookingWindow: '30',
  autoConfirm: true,
  preventPastScheduling: true,

  // Notifications
  smsReminders: true,
  queueAlerts: true,
  emailAlerts: true,
};

export const settingsService = {
  /**
   * Fetch all operational preferences and settings for a clinic
   * GET /hospital/{hospitalId}/clinics/{clinicId}
   */
  getClinicSettings: async (clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const storageKey = `pms_clinic_settings_${activeClinicId}`;

    let cachedSettings = {};
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        cachedSettings = JSON.parse(stored) || {};
      }
    } catch (e) {
      // ignore JSON parse error
    }

    try {
      const res = await apiClient.get(`/hospital/${activeHospId}/clinics/${activeClinicId}`);
      const data = res.data || res || {};

      let parsedPrefs = {};
      if (typeof data.preferences === 'string') {
        try {
          parsedPrefs = JSON.parse(data.preferences) || {};
        } catch (e) {
          parsedPrefs = {};
        }
      } else if (typeof data.preferences === 'object' && data.preferences !== null) {
        parsedPrefs = data.preferences;
      }

      const merged = {
        ...DEFAULT_CLINIC_SETTINGS,
        ...cachedSettings,
        ...parsedPrefs,
        name: data.name || cachedSettings.name || 'Active Clinic',
        operatingHours: data.operatingHours || parsedPrefs.operatingHours || cachedSettings.operatingHours || DEFAULT_CLINIC_SETTINGS.operatingHours,
        roomNumbers: Array.isArray(data.roomNumbers)
          ? data.roomNumbers.join(', ')
          : (data.roomNumbers || parsedPrefs.roomNumbers || cachedSettings.roomNumbers || DEFAULT_CLINIC_SETTINGS.roomNumbers),
      };

      localStorage.setItem(storageKey, JSON.stringify(merged));
      return merged;
    } catch (err) {
      console.warn(`Could not load clinic settings from backend (${err.message}), using cached/defaults`);
      return {
        ...DEFAULT_CLINIC_SETTINGS,
        ...cachedSettings,
      };
    }
  },

  /**
   * Update settings for a clinic (persists to backend and local storage)
   * PUT /hospital/{hospitalId}/clinics/{clinicId}
   */
  saveClinicSettings: async (clinicId, hospitalId, updatedFields) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);
    const storageKey = `pms_clinic_settings_${activeClinicId}`;

    // Read current cached settings
    let currentSettings = { ...DEFAULT_CLINIC_SETTINGS };
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        currentSettings = { ...currentSettings, ...JSON.parse(stored) };
      }
    } catch (e) {
      // ignore
    }

    const merged = { ...currentSettings, ...updatedFields };
    localStorage.setItem(storageKey, JSON.stringify(merged));

    // Prepare backend payload for ClinicController
    const roomNumbersArray = Array.isArray(merged.roomNumbers)
      ? merged.roomNumbers
      : String(merged.roomNumbers || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

    const preferencesObj = {
      allowWalkIn: Boolean(merged.allowWalkIn),
      triageWaitAlert: Boolean(merged.triageWaitAlert),
      slotDuration: String(merged.slotDuration || '30'),
      advanceBookingWindow: String(merged.advanceBookingWindow || '30'),
      autoConfirm: Boolean(merged.autoConfirm),
      preventPastScheduling: Boolean(merged.preventPastScheduling),
      smsReminders: Boolean(merged.smsReminders),
      queueAlerts: Boolean(merged.queueAlerts),
      emailAlerts: Boolean(merged.emailAlerts),
    };

    const payload = {
      id: String(activeClinicId),
      name: merged.name || `Clinic #${activeClinicId}`,
      operatingHours: merged.operatingHours,
      roomNumbers: roomNumbersArray,
      preferences: JSON.stringify(preferencesObj),
    };

    try {
      const res = await apiClient.put(`/hospital/${activeHospId}/clinics/${activeClinicId}`, payload);
      const data = res.data || res;
      return {
        ...merged,
        ...(data || {}),
      };
    } catch (err) {
      console.warn('Backend settings update notice:', err.message);
      // Even if backend fails, locally cached settings ensure state is preserved
      return merged;
    }
  },

  /**
   * Update user profile
   * PUT /users/{userId} (or platform service)
   */
  updateProfile: async (userId, data) => {
    const payload = {
      firstName: data.firstName?.trim(),
      lastName: data.lastName?.trim(),
      phone: data.phone?.trim(),
    };

    try {
      // First attempt: Platform service
      const res = await axios.put(`${PLATFORM_BASE_URL}/users/${userId}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      return res.data?.data || res.data;
    } catch (err) {
      try {
        // Second attempt: Direct PMS/Platform proxy
        const res = await apiClient.put(`/users/${userId}`, payload);
        return res.data?.data || res.data;
      } catch (innerErr) {
        console.warn('User profile backend update notice:', innerErr.message);
        return payload;
      }
    }
  },
};

export default settingsService;

